import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withKeys as _withKeys, withModifiers as _withModifiers, vModelSelect as _vModelSelect, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "staff-page" }
const _hoisted_2 = { class: "page-heading" }
const _hoisted_3 = { class: "px-5 pt-5" }
const _hoisted_4 = { class: "table-toolbar" }
const _hoisted_5 = {
  class: "segmented-control",
  role: "group",
  "aria-label": "Период опросов"
}
const _hoisted_6 = { class: "ui-field table-search" }
const _hoisted_7 = {
  key: 0,
  role: "alert",
  class: "ui-alert ui-alert--error mx-5 mb-5"
}
const _hoisted_8 = {
  key: 0,
  class: "ui-empty"
}
const _hoisted_9 = {
  key: 1,
  class: "ui-table-scroll"
}
const _hoisted_10 = { class: "ui-table survey-table" }
const _hoisted_11 = { class: "num" }
const _hoisted_12 = { class: "num" }
const _hoisted_13 = ["aria-label", "onClick", "onKeydown"]
const _hoisted_14 = { class: "num whitespace-nowrap" }
const _hoisted_15 = { class: "font-medium" }
const _hoisted_16 = { class: "num" }
const _hoisted_17 = { class: "score-cell" }
const _hoisted_18 = { class: "mark" }
const _hoisted_19 = { class: "mark" }
const _hoisted_20 = { key: 0 }
const _hoisted_21 = { class: "table-pagination" }
const _hoisted_22 = { class: "flex items-center gap-2 text-xs text-muted-foreground" }
const _hoisted_23 = ["value"]

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ArrowDownUp, MessageSquare, NotebookPen, ChevronLeft, ChevronRight } from '@lucide/vue';

import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import { dateTimeToDateWithTimeString } from '@/filters/date_filters';
import SurveyChart from '@/components/SurveyChart.vue';
import SurveyDetailDrawer from '@/components/SurveyDetailDrawer.vue';

interface Survey {
  id: number;
  date: string;
  property_name: string;
  property_id?: number;
  position: string;
  contact_name: string;
  contact_person_id?: number;
  vote: number;
  notes: string;
  comments: string;
  ip: string | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SurveyList',
  setup(__props) {

const route = useRoute();
const propertyName = ref('');
const propertyFilterValid = computed(() => {
  const value = route.query.property_id;
  return (
    value === undefined ||
    (typeof value === 'string' &&
      /^\d+$/.test(value) &&
      Number.isSafeInteger(Number(value)) &&
      Number(value) > 0)
  );
});
const propertyId = computed(() =>
  propertyFilterValid.value && route.query.property_id !== undefined
    ? Number(route.query.property_id)
    : null
);

const loading = ref(true);
const search = ref('');
const surveys = ref<Survey[]>([]);
const totalCount = ref(0);
const currentPage = ref(1);
const itemsPerPage = ref(50);
const sortBy = ref([{ key: 'date', order: 'desc' as 'asc' | 'desc' }]);
const dateFilter = ref('all_time');
const drawerOpen = ref(false);
const selectedSurvey = ref<Survey | null>(null);
const mainTab = ref('list');
const errorMessage = ref<string | null>(null);

let searchTimeout: number | null = null;
let latestRequest = 0;

async function loadPropertyName() {
  const id = propertyId.value;
  propertyName.value = '';
  if (!id) return;
  try {
    const response = await axios.get(`/api/properties/${id}/`);
    if (propertyId.value === id) propertyName.value = response.data.name;
  } catch {
    // The survey request reports access and loading errors; keep the object ID as a fallback.
  }
}

function formatDateTime(dateTime: string): string {
  return dateTimeToDateWithTimeString(dateTime) || '';
}

function debouncedSearch() {
  if (searchTimeout !== null) {
    clearTimeout(searchTimeout);
  }
  searchTimeout = window.setTimeout(() => {
    currentPage.value = 1;
    loadSurveys();
  }, 500);
}

function onDateFilterChange() {
  currentPage.value = 1;
  loadSurveys();
}

async function loadSurveys() {
  const requestId = ++latestRequest;
  loading.value = true;
  errorMessage.value = null;
  if (!propertyFilterValid.value) {
    errorMessage.value = 'Некорректный объект в ссылке на NPS-оценки.';
    surveys.value = [];
    totalCount.value = 0;
    loading.value = false;
    return;
  }
  try {
    const params: any = {
      limit: itemsPerPage.value === -1 ? 10000 : itemsPerPage.value,
      offset: itemsPerPage.value === -1 ? 0 : (currentPage.value - 1) * itemsPerPage.value,
    };

    if (propertyId.value) {
      params.property_id = propertyId.value;
    }

    if (search.value) {
      params.search = search.value;
    }

    // Добавляем фильтр по дате
    if (dateFilter.value && dateFilter.value !== 'all_time') {
      params.date_filter = dateFilter.value;
    }

    // Добавляем сортировку
    if (sortBy.value && sortBy.value.length > 0) {
      const sortItem = sortBy.value[0] as { key: string; order: 'asc' | 'desc' };
      const ordering = sortItem.order === 'desc' ? '-' : '';
      params.ordering = `${ordering}${sortItem.key}`;
    }

    const response = await axios.get('/api/surveys/', { params });
    if (requestId !== latestRequest) return;
    const data = response.data as PagedList<Survey>;
    surveys.value = data.results as Survey[];
    totalCount.value = data.count;
  } catch (e: any) {
    if (requestId !== latestRequest) return;
    if (e.response?.status === 403) {
      // User doesn't have permission
      errorMessage.value =
        'У вас нет прав для просмотра списка опросов. Обратитесь к администратору для получения доступа.';
      surveys.value = [];
      totalCount.value = 0;
    } else if (e.response?.status === 401) {
      errorMessage.value = 'Сессия истекла. Пожалуйста, войдите снова.';
      surveys.value = [];
      totalCount.value = 0;
    } else {
      errorMessage.value = 'Ошибка при загрузке данных. Пожалуйста, попробуйте позже.';
      console.error('Error loading surveys:', e);
      surveys.value = [];
      totalCount.value = 0;
    }
  } finally {
    if (requestId === latestRequest) loading.value = false;
  }
}

async function openSurveyDrawer(event: any, { item }: { item: Survey }) {
  surveyTrigger.value = event.currentTarget as HTMLElement;
  selectedSurvey.value = item;
  drawerOpen.value = true;
}

const surveyTrigger = ref<HTMLElement | null>(null);
watch(drawerOpen, (open) => {
  if (!open) requestAnimationFrame(() => surveyTrigger.value?.focus());
});

watch(
  () => route.query.property_id,
  () => {
    currentPage.value = 1;
    drawerOpen.value = false;
    selectedSurvey.value = null;
    surveys.value = [];
    totalCount.value = 0;
    loadPropertyName();
    loadSurveys();
  }
);

watch(
  () => search.value,
  () => {
    currentPage.value = 1;
    debouncedSearch();
  }
);

onMounted(async () => {
  loadPropertyName();
  await loadSurveys();
});

onBeforeUnmount(() => {
  latestRequest++;
  if (searchTimeout !== null) clearTimeout(searchTimeout);
});

const surveyPeriods = [
  { value: 'all_time', label: 'За всё время' },
  { value: 'last_6_months', label: 'Последние полгода' },
  { value: 'last_3_months', label: 'Последние 3 месяца' },
];
const surveyPageCount = computed(() =>
  itemsPerPage.value === -1 ? 1 : Math.max(1, Math.ceil(totalCount.value / itemsPerPage.value))
);
function sortSurveys(key: string) {
  sortBy.value = [
    {
      key,
      order: sortBy.value[0]?.key === key && sortBy.value[0]?.order === 'asc' ? 'desc' : 'asc',
    },
  ];
  currentPage.value = 1;
  loadSurveys();
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _createElementVNode("div", null, [
        _cache[9] || (_cache[9] = _createElementVNode("div", { class: "page-eyebrow" }, "Обратная связь", -1)),
        _createElementVNode("h1", null, _toDisplayString(propertyId.value ? `NPS-оценки · ${propertyName.value || `Объект #${propertyId.value}`}` : 'Опросы'), 1)
      ]),
      (propertyId.value)
        ? (_openBlock(), _createBlock(_unref(Button), {
            key: 0,
            "as-child": "",
            variant: "outline",
            size: "sm"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_router_link, { to: { name: 'surveys' } }, {
                default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                  _createTextVNode("Все объекты", -1)
                ]))]),
                _: 1
              })
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    _createVNode(_unref(Card), null, {
      default: _withCtx(() => [
        _createVNode(_unref(Tabs), {
          modelValue: mainTab.value,
          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((mainTab).value = $event))
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_3, [
              _createVNode(_unref(TabsList), null, {
                default: _withCtx(() => [
                  _createVNode(_unref(TabsTrigger), { value: "list" }, {
                    default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                      _createTextVNode("Список", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_unref(TabsTrigger), { value: "dynamics" }, {
                    default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                      _createTextVNode("Динамика", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _createVNode(_unref(TabsContent), { value: "list" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("div", _hoisted_5, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(surveyPeriods, (period) => {
                      return _createVNode(_unref(Button), {
                        key: period.value,
                        size: "sm",
                        variant: dateFilter.value === period.value ? 'secondary' : 'ghost',
                        "aria-pressed": dateFilter.value === period.value,
                        onClick: ($event: any) => {
                  dateFilter.value = period.value;
                  onDateFilterChange();
                }
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(period.label), 1)
                        ]),
                        _: 2
                      }, 1032, ["variant", "aria-pressed", "onClick"])
                    }), 64))
                  ]),
                  _createElementVNode("label", _hoisted_6, [
                    _cache[13] || (_cache[13] = _createElementVNode("span", { class: "sr-only" }, "Поиск по объекту или ФИО", -1)),
                    _createVNode(_unref(Input), {
                      modelValue: search.value,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
                      type: "search",
                      placeholder: "Поиск по объекту или ФИО"
                    }, null, 8, ["modelValue"])
                  ])
                ]),
                (errorMessage.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(errorMessage.value), 1))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      (loading.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                            _createVNode(_unref(Spinner)),
                            _cache[14] || (_cache[14] = _createElementVNode("span", null, "Загрузка опросов…", -1))
                          ]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                            _createElementVNode("table", _hoisted_10, [
                              _createElementVNode("thead", null, [
                                _createElementVNode("tr", null, [
                                  _createElementVNode("th", _hoisted_11, [
                                    _createVNode(_unref(Button), {
                                      variant: "ghost",
                                      size: "sm",
                                      class: "table-sort",
                                      onClick: _cache[1] || (_cache[1] = ($event: any) => (sortSurveys('date')))
                                    }, {
                                      default: _withCtx(() => [
                                        _cache[15] || (_cache[15] = _createTextVNode(" Дата ", -1)),
                                        _createVNode(_unref(ArrowDownUp), { size: 13 })
                                      ]),
                                      _: 1
                                    })
                                  ]),
                                  _cache[17] || (_cache[17] = _createElementVNode("th", null, "Объект", -1)),
                                  _cache[18] || (_cache[18] = _createElementVNode("th", null, "Должность", -1)),
                                  _cache[19] || (_cache[19] = _createElementVNode("th", null, "ФИО", -1)),
                                  _createElementVNode("th", _hoisted_12, [
                                    _createVNode(_unref(Button), {
                                      variant: "ghost",
                                      size: "sm",
                                      class: "table-sort",
                                      onClick: _cache[2] || (_cache[2] = ($event: any) => (sortSurveys('vote')))
                                    }, {
                                      default: _withCtx(() => [
                                        _cache[16] || (_cache[16] = _createTextVNode(" Оценка ", -1)),
                                        _createVNode(_unref(ArrowDownUp), { size: 13 })
                                      ]),
                                      _: 1
                                    })
                                  ]),
                                  _cache[20] || (_cache[20] = _createElementVNode("th", null, [
                                    _createElementVNode("span", { class: "sr-only" }, "Комментарий")
                                  ], -1))
                                ])
                              ]),
                              _createElementVNode("tbody", null, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(surveys.value, (item) => {
                                  return (_openBlock(), _createElementBlock("tr", {
                                    key: item.id,
                                    class: "survey-row",
                                    tabindex: "0",
                                    "aria-label": 'Отзыв: ' + item.contact_name,
                                    onClick: ($event: any) => (openSurveyDrawer($event, { item })),
                                    onKeydown: [
                                      _withKeys(($event: any) => (openSurveyDrawer($event, { item })), ["enter"]),
                                      _withKeys(_withModifiers(($event: any) => (openSurveyDrawer($event, { item })), ["prevent"]), ["space"])
                                    ]
                                  }, [
                                    _createElementVNode("td", _hoisted_14, _toDisplayString(formatDateTime(item.date)), 1),
                                    _createElementVNode("td", _hoisted_15, _toDisplayString(item.property_name), 1),
                                    _createElementVNode("td", null, _toDisplayString(item.position), 1),
                                    _createElementVNode("td", null, _toDisplayString(item.contact_name), 1),
                                    _createElementVNode("td", _hoisted_16, [
                                      _createElementVNode("span", _hoisted_17, [
                                        _createElementVNode("span", _hoisted_18, [
                                          (item.notes)
                                            ? (_openBlock(), _createBlock(_unref(MessageSquare), {
                                                key: 0,
                                                size: 14,
                                                title: "Есть отзыв",
                                                class: "text-muted-foreground"
                                              }))
                                            : _createCommentVNode("", true)
                                        ]),
                                        _createVNode(_unref(Badge), {
                                          variant: 
                            item.vote >= 9
                              ? 'success'
                              : item.vote >= 7
                                ? 'secondary'
                                : item.vote >= 5
                                  ? 'warning'
                                  : 'destructive'
                          
                                        }, {
                                          default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(item.vote), 1)
                                          ]),
                                          _: 2
                                        }, 1032, ["variant"])
                                      ])
                                    ]),
                                    _createElementVNode("td", null, [
                                      _createElementVNode("span", _hoisted_19, [
                                        (item.comments)
                                          ? (_openBlock(), _createBlock(_unref(NotebookPen), {
                                              key: 0,
                                              size: 15,
                                              title: "Есть комментарий менеджера",
                                              class: "text-primary"
                                            }))
                                          : _createCommentVNode("", true)
                                      ])
                                    ])
                                  ], 40, _hoisted_13))
                                }), 128)),
                                (!surveys.value.length)
                                  ? (_openBlock(), _createElementBlock("tr", _hoisted_20, [...(_cache[21] || (_cache[21] = [
                                      _createElementVNode("td", {
                                        colspan: "6",
                                        class: "text-center text-muted-foreground py-8"
                                      }, " Опросы не найдены ", -1)
                                    ]))]))
                                  : _createCommentVNode("", true)
                              ])
                            ])
                          ])),
                      _createElementVNode("div", _hoisted_21, [
                        _createElementVNode("label", _hoisted_22, [
                          _cache[22] || (_cache[22] = _createTextVNode(" Строк ", -1)),
                          _withDirectives(_createElementVNode("select", {
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((itemsPerPage).value = $event)),
                            class: "ui-select",
                            "aria-label": "Строк на странице",
                            onChange: _cache[4] || (_cache[4] = ($event: any) => {
                    currentPage.value = 1;
                    loadSurveys();
                  })
                          }, [
                            (_openBlock(), _createElementBlock(_Fragment, null, _renderList([25, 50, 75, 100, -1], (size) => {
                              return _createElementVNode("option", {
                                key: size,
                                value: size
                              }, _toDisplayString(size === -1 ? 'Все' : size), 9, _hoisted_23)
                            }), 64))
                          ], 544), [
                            [
                              _vModelSelect,
                              itemsPerPage.value,
                              void 0,
                              { number: true }
                            ]
                          ])
                        ]),
                        _createElementVNode("span", null, _toDisplayString(totalCount.value) + " отзывов", 1),
                        _createVNode(_unref(Button), {
                          variant: "outline",
                          size: "icon",
                          "aria-label": "Предыдущая страница",
                          disabled: currentPage.value === 1 || loading.value,
                          onClick: _cache[5] || (_cache[5] = ($event: any) => {
                  currentPage.value--;
                  loadSurveys();
                })
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(ChevronLeft), { size: 16 })
                          ]),
                          _: 1
                        }, 8, ["disabled"]),
                        _createElementVNode("span", null, _toDisplayString(currentPage.value) + " / " + _toDisplayString(surveyPageCount.value), 1),
                        _createVNode(_unref(Button), {
                          variant: "outline",
                          size: "icon",
                          "aria-label": "Следующая страница",
                          disabled: currentPage.value >= surveyPageCount.value || loading.value,
                          onClick: _cache[6] || (_cache[6] = ($event: any) => {
                  currentPage.value++;
                  loadSurveys();
                })
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(ChevronRight), { size: 16 })
                          ]),
                          _: 1
                        }, 8, ["disabled"])
                      ])
                    ], 64))
              ]),
              _: 1
            }),
            _createVNode(_unref(TabsContent), {
              value: "dynamics",
              class: "p-5"
            }, {
              default: _withCtx(() => [
                (propertyFilterValid.value && !errorMessage.value)
                  ? (_openBlock(), _createBlock(SurveyChart, {
                      key: propertyId.value || 'all',
                      "property-id": propertyId.value,
                      title: propertyId.value ? 'Динамика оценок объекта' : 'Динамика оценок по всем объектам'
                    }, null, 8, ["property-id", "title"]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue"])
      ]),
      _: 1
    }),
    _createVNode(SurveyDetailDrawer, {
      modelValue: drawerOpen.value,
      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((drawerOpen).value = $event)),
      survey: selectedSurvey.value,
      onSurveyUpdated: loadSurveys
    }, null, 8, ["modelValue", "survey"])
  ]))
}
}

})